import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  ArrowRight,
  ArrowUp,
  DollarSign,
  MapPin,
  Users,
  Globe,
  Heart,
  Rocket,
  GraduationCap,
  Briefcase,
} from "lucide-react";
import _route from "../constants/routes";
import heroBg from "../assets/landing/hero-bg.jpg";
import hdfLogo from "../assets/landing/hdf-logo.png";
import whyBg from "../assets/landing/why-it-matters-bg.jpg";
import ctaBg from "../assets/landing/final-cta-bg.jpg";

const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 200]);
  const overlayOpacity = useTransform(scrollY, [0, 600], [0.35, 0.7]);
  const contentY = useTransform(scrollY, [0, 500], [0, -60]);
  const contentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section className="relative min-h-screen flex items-start justify-center overflow-hidden">
      <motion.div
        className="absolute inset-0 parallax-bg"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 18, ease: "easeOut" }}
        style={{ y: bgY }}
      >
        <img
          src={heroBg}
          alt="HDF volunteer lifting a child surrounded by joyful community"
          className="w-full h-full object-cover object-[center_30%]"
          style={{ filter: "contrast(1.12) brightness(0.93)" }}
        />
      </motion.div>

      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(13,87,127,0.6) 0%, rgba(13,87,127,0.35) 40%, rgba(13,87,127,0.35) 60%, rgba(13,87,127,0.55) 100%)",
          opacity: overlayOpacity,
        }}
      />
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 150px 60px rgba(0,0,0,0.25)" }} />

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 0.9, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        className="absolute top-6 z-20 w-full flex justify-center sm:w-auto sm:left-10 sm:justify-start"
      >
        <img src={hdfLogo} alt="HDF Logo" className="w-auto h-12 sm:h-16 md:h-20" />
      </motion.div>

      <motion.div
        className="relative z-10 container mx-auto px-4 sm:px-6 text-center max-w-3xl pt-[18vh] sm:pt-[18vh]"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mb-8"
        >
          <span className="inline-block px-5 py-2.5 rounded-full border border-white/20 text-white/85 text-sm font-body tracking-wider uppercase backdrop-blur-sm">
            The Human Development Lab
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 0.92, y: 0 }}
          transition={{ duration: 1.1, delay: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="font-display font-bold text-white leading-[1.1] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4"
          style={{ textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
        >
          What if your idea didn't just stay an idea...
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
          className="font-accent text-secondary mb-8 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.3), 0 0 20px hsl(49 86% 54% / 0.2)" }}
        >
          ...but changed a life?
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="text-base sm:text-xl md:text-2xl text-white/95 max-w-lg mx-auto mb-10 sm:mb-16 font-body font-semibold leading-relaxed"
          style={{ textShadow: "0 1px 6px rgba(0,0,0,0.3)" }}
        >
          Design, fund, and implement a real humanitarian project — and see it impact lives.
        </motion.p>

        <div className="relative">
          <div
            className="absolute -inset-x-16 -inset-y-8 -z-10 pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.25) 0%, transparent 70%)" }}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.5 }}
            className="flex flex-col sm:flex-row gap-3.5 sm:gap-4 justify-center items-stretch"
          >
            <Link
              to={_route._signup}
              className="group inline-flex items-center justify-center gap-2.5 px-9 sm:px-12 py-4 font-display font-bold rounded-xl text-base sm:text-lg text-secondary-foreground shadow-[0_4px_20px_hsl(49_86%_54%/0.35),0_2px_8px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_32px_hsl(49_86%_54%/0.5),0_2px_12px_rgba(0,0,0,0.15)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
              style={{ background: "linear-gradient(to bottom, hsl(49 88% 58%), hsl(49 86% 48%))" }}
            >
              Apply Now
              <ArrowRight className="group-hover:translate-x-1 transition-transform duration-200" size={20} />
            </Link>
            <Link
              to={_route._login}
              className="inline-flex items-center justify-center px-9 sm:px-12 py-4 bg-white/90 text-primary font-display font-semibold rounded-xl text-base sm:text-lg shadow-[0_2px_12px_rgba(0,0,0,0.08)] hover:bg-white hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] transition-all duration-200 hover:scale-[1.02] active:scale-[0.97]"
            >
              Sign In
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer z-10"
        onClick={() => scrollToId("emotional-hook")}
      >
        <span className="text-white/80 text-xs font-body tracking-widest uppercase">Scroll</span>
        <ChevronDown className="text-white/80 animate-scroll-hint" size={22} />
      </motion.div>
    </section>
  );
};

/* ---------------- HDF Intro ---------------- */
const HdfIntro = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="pt-16 sm:pt-24 pb-8 sm:pb-12 bg-background" ref={ref}>
      <div className="container mx-auto px-6 max-w-3xl">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="font-display font-bold text-foreground text-2xl sm:text-4xl md:text-5xl leading-tight tracking-tight mb-6 sm:mb-8"
        >
          We go where the need is greatest.
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-base sm:text-lg md:text-xl font-body leading-relaxed text-muted-foreground space-y-4"
        >
          <p>Human Development Fund (HDF) is a humanitarian organization delivering relief, health, education, water, and development programs across 20+ countries in Africa, the Middle East, and South Asia.</p>
          <p>We work directly in communities — building wells, supporting orphans, running health clinics, feeding families, and rebuilding lives after disaster.</p>
          <p>Every project is documented in real time so our donors and community always know exactly where their support goes.</p>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Emotional Hook ---------------- */
const EmotionalHook = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const lines = [
    { text: "What if your idea...", delay: 0 },
    { text: "fed a family?", delay: 0.25 },
    { text: "built a well?", delay: 0.5 },
    { text: null, accent: "changed a life?", delay: 0.8 },
  ];
  return (
    <section id="emotional-hook" className="pt-4 sm:pt-8 pb-12 sm:pb-16 bg-background">
      <div className="container mx-auto px-6 max-w-3xl" ref={ref}>
        <div className="space-y-2 sm:space-y-6 mb-10 sm:mb-20">
          {lines.map((line, i) => (
            <motion.h2
              key={i}
              initial={{ opacity: 0, x: -40, filter: "blur(8px)" }}
              animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
              transition={{ duration: 0.8, delay: line.delay, ease: [0.25, 0.1, 0.25, 1] }}
              className={
                line.accent
                  ? "font-accent text-secondary text-3xl sm:text-5xl md:text-7xl leading-tight text-shimmer"
                  : "text-xl sm:text-4xl md:text-6xl font-display font-black text-foreground leading-tight tracking-tight"
              }
            >
              {line.text || line.accent}
            </motion.h2>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, x: -20, scaleX: 0.3 }}
          animate={inView ? { opacity: 1, x: 0, scaleX: 1 } : {}}
          transition={{ duration: 0.7, delay: 1.1, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ transformOrigin: "left" }}
          className="border-l-[5px] border-secondary pl-6 mb-6 sm:mb-10"
        >
          <p className="text-lg sm:text-2xl font-display leading-relaxed" style={{ color: "#5F7682" }}>
            This isn't hypothetical.
            <br />
            This is <span className="text-foreground font-bold">real impact</span> — and it starts with{" "}
            <span className="text-foreground font-bold">you</span>.
          </p>
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1.3 }}
          className="text-base sm:text-xl font-body font-medium"
          style={{ color: "#5F7682" }}
        >
          No experience needed. Just an idea worth building.
        </motion.p>
      </div>
    </section>
  );
};

/* ---------------- What This Is ---------------- */
const WHAT_STEPS = [
  { num: "01", title: "Real Problems", desc: "Tackle genuine humanitarian challenges in communities that need it most." },
  { num: "02", title: "Real Funding", desc: "HDF contributes up to $5,000 to help you reach your project goal — you raise the rest." },
  { num: "03", title: "Real Implementation", desc: "Go beyond theory — build, launch, and deliver your project on the ground." },
  { num: "04", title: "Real Impact", desc: "See the faces of the people whose lives you've changed. Forever." },
];

const WhatThisIs = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="section-padding bg-muted/50" ref={ref}>
      <div className="container mx-auto px-6 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            This isn't a competition. It's a{" "}
            <span className="font-accent text-secondary text-4xl sm:text-5xl md:text-6xl">calling.</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto font-body" style={{ color: "#5F7682" }}>
            A real-world challenge where participants design, fund, and execute humanitarian solutions that transform communities — starting with you.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-6">
          {WHAT_STEPS.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.12 * i }}
            >
              <span className="block font-display font-black text-5xl mb-4 text-primary/25">{step.num}</span>
              <h3 className="text-lg font-display font-bold text-foreground mb-2">{step.title}</h3>
              <p className="font-body text-sm leading-relaxed" style={{ color: "#5F7682" }}>{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- How It Works (timeline) ---------------- */
const ROUNDS = [
  {
    title: "Submit Your Idea",
    desc: "Identify a real humanitarian problem and submit your proposed solution.",
    requirementsLabel: "If you advance, you will need to provide",
    requirements: [
      "A written proposal covering your problem statement, proposed intervention, target population, implementation plan, budget, and anticipated outcomes",
      "Optional supporting documents — research, references, or data that inform your proposal (PDF, max 3 files)",
      "A signed participation agreement before your submission can be accepted",
    ],
  },
  {
    title: "Internal Review",
    desc: "Applications are evaluated based on impact, feasibility, and alignment with mission.",
    requirementsLabel: "If you advance, you will need to provide",
    requirements: [
      "A recorded video pitch presenting your proposal — no live component, record on your own time",
      "An optional slide deck (PDF) to accompany your video",
      "There is no required length — say what needs to be said clearly and compellingly",
    ],
  },
  {
    title: "Finalist Pitch",
    desc: "Shortlisted applicants present their recorded pitch to a panel of impact leaders.",
    requirementsLabel: "If you advance, you will need to provide",
    requirements: [
      "A detailed budget and legal review of your proposal — you may be asked to revise or clarify elements",
      "Signing of the implementation agreement with HDF legal counsel",
      "Independent fundraising toward your project budget — HDF contributes up to $5,000 to help you reach your goal",
      "You may also fundraise for your own round-trip travel to Kenya",
    ],
  },
  {
    title: "Implement Your Project",
    desc: "Selected participants receive mentorship and begin implementing their projects with HDF support.",
    requirementsLabel: "If you advance, you will need to provide",
    requirements: [
      "Regular virtual check-ins with HDF mentors throughout the 6-month implementation window",
      "Access to HDF's Kenya office and ground team for on-the-ground coordination",
      "Milestone updates and documentation submitted to HDF as your project progresses",
    ],
  },
  {
    title: "Travel & See Impact",
    desc: "Participants travel to Kenya to witness their project's impact firsthand and celebrate its completion.",
  },
];

const RoundItem = ({ round, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [expanded, setExpanded] = useState(false);
  const hasReqs = round.requirements && round.requirements.length > 0;
  const isLeft = i % 2 === 0;

  const Body = (
    <div className="group">
      <span className="text-xs font-body text-muted-foreground uppercase tracking-widest">Round {i + 1}</span>
      <h3 className="text-xl font-display font-bold text-foreground mt-1">{round.title}</h3>
      <p className="text-muted-foreground font-body mt-2 text-sm leading-relaxed">{round.desc}</p>
      {hasReqs && (
        <>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="mt-4 flex items-center gap-1.5 text-xs font-body uppercase tracking-widest text-secondary hover:opacity-80 transition-colors"
          >
            <ChevronDown size={14} className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
            {expanded ? "Hide details" : "What you'll need"}
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-border/40">
                  <p className="text-[11px] font-body uppercase tracking-widest text-muted-foreground mb-3">{round.requirementsLabel}</p>
                  <ul className="space-y-2.5">
                    {round.requirements.map((req, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm font-body text-muted-foreground leading-relaxed">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary/60 flex-shrink-0" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );

  return (
    <div ref={ref} className="relative grid grid-cols-[2rem_1fr] md:grid-cols-[1fr_2rem_1fr] items-start gap-0">
      <div className="hidden md:block">
        {isLeft ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="pr-12 text-left"
          >
            {Body}
          </motion.div>
        ) : (
          <div />
        )}
      </div>
      <div className="flex justify-center relative">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay: 0.15 }}
          className="w-4 h-4 rounded-full bg-secondary border-4 border-background z-10 mt-1"
        />
      </div>
      <div className="md:hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          className="pl-4"
        >
          {Body}
        </motion.div>
      </div>
      <div className="hidden md:block">
        {!isLeft ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="pl-12"
          >
            {Body}
          </motion.div>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

const HowItWorks = () => {
  const headingRef = useRef(null);
  const headingInView = useInView(headingRef, { once: true, margin: "-80px" });
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: timelineRef, offset: ["start 0.85", "end 0.7"] });
  const lineHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <section id="how-it-works" className="section-padding bg-background">
      <div className="container mx-auto px-6 max-w-5xl">
        <motion.div
          ref={headingRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headingInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-foreground mb-4">How It Works</h2>
          <p className="text-lg text-muted-foreground font-body">Five rounds from idea to real-world impact.</p>
        </motion.div>
        <div className="relative flex flex-col gap-16" ref={timelineRef}>
          <div className="absolute left-[1rem] md:left-1/2 top-0 bottom-0 w-px bg-border/40 -translate-x-1/2" />
          <motion.div
            className="absolute left-[1rem] md:left-1/2 top-0 w-px bg-secondary/60 -translate-x-1/2 origin-top"
            style={{ height: lineHeight }}
          />
          {ROUNDS.map((r, i) => (
            <RoundItem key={r.title} round={r} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Why It Matters ---------------- */
const WhyItMatters = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section className="relative py-14 sm:py-20 flex items-center overflow-hidden" ref={ref}>
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={whyBg} alt="" className="w-full h-[120%] object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-primary/85" />
      <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-black text-primary-foreground tracking-tight"
        >
          Most people talk about change.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-black text-primary-foreground/60 mt-3 tracking-tight"
        >
          Few actually{" "}
          <span className="font-accent text-secondary text-2xl sm:text-4xl md:text-5xl lg:text-6xl text-shimmer">build it.</span>
        </motion.p>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-20 h-0.5 bg-secondary/60 mx-auto mt-8 origin-center"
        />
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="text-lg text-primary-foreground/80 font-body mt-6"
        >
          Turn your idea into real impact.
        </motion.p>
      </div>
    </section>
  );
};

/* ---------------- Benefits ---------------- */
const BENEFITS = [
  { Icon: DollarSign, title: "Up to $5,000 from HDF", desc: "You set your budget, raise what you need, and HDF contributes up to $5,000 to help get you there." },
  { Icon: MapPin, title: "Trip to Kenya", desc: "See your project come to life on the ground." },
  { Icon: Users, title: "Mentorship from HDF", desc: "Guided by seasoned humanitarian leaders." },
  { Icon: Globe, title: "Global Recognition", desc: "Showcase your impact to a worldwide audience." },
  { Icon: Heart, title: "Real-World Impact", desc: "An experience that changes you — and others — forever." },
  { Icon: Rocket, title: "Launch Your Own Initiative", desc: "Turn your vision into action and lead a project from concept to execution." },
];

const AnimatedCounter = ({ target, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);
  return (
    <span ref={ref}>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

const Benefits = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="section-padding bg-muted/30" ref={ref}>
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-foreground mb-4">What You Get</h2>
          <p className="text-lg text-muted-foreground font-body">More than funding — a real-world experience that shapes you.</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex justify-center gap-6 sm:gap-8 mb-10 mx-auto"
          style={{ maxWidth: 550 }}
        >
          <div className="text-center flex-1">
            <div className="text-3xl sm:text-4xl font-display font-bold text-secondary">$<AnimatedCounter target={5000} /></div>
            <p className="text-[10px] text-muted-foreground/60 font-body mt-1 uppercase tracking-[0.12em]">from HDF</p>
          </div>
          <div className="text-center flex-1">
            <div className="text-3xl sm:text-4xl font-display font-bold text-primary"><AnimatedCounter target={6} /></div>
            <p className="text-[10px] text-muted-foreground/60 font-body mt-1 uppercase tracking-[0.12em]">months of impact</p>
          </div>
          <div className="text-center flex-1">
            <div className="text-3xl sm:text-4xl font-display font-bold text-secondary"><AnimatedCounter target={1} /></div>
            <p className="text-[10px] text-muted-foreground/60 font-body mt-1 uppercase tracking-[0.12em]">winner</p>
          </div>
        </motion.div>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-24 h-px bg-border/60 mx-auto mb-10 origin-center"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.08 * i, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.25 } }}
              className="group relative rounded-xl p-5 border border-border bg-card hover:border-secondary/40 hover:shadow-[0_8px_30px_-8px_hsl(49_86%_54%/0.12)] transition-all duration-300"
            >
              <motion.div
                whileHover={{ rotate: [0, -8, 8, 0], transition: { duration: 0.5 } }}
                className="w-10 h-10 rounded-lg bg-secondary/8 flex items-center justify-center mb-3 group-hover:bg-secondary/15 transition-colors"
              >
                <b.Icon className="text-secondary" size={20} />
              </motion.div>
              <h3 className="text-lg font-display font-bold text-foreground mb-1">{b.title}</h3>
              <p className="text-sm text-muted-foreground font-body leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.blockquote
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 max-w-[600px] mx-auto text-center relative"
        >
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 text-9xl font-display text-border/50 leading-none select-none pointer-events-none" aria-hidden="true">&ldquo;</span>
          <p className="text-lg font-body italic text-muted-foreground" style={{ lineHeight: 1.75 }}>
            I came in with an idea. I left with a <span className="font-accent not-italic text-secondary/85 text-2xl">purpose.</span> This challenge didn't just change a community — it changed me.
          </p>
          <footer className="mt-3 text-xs font-display font-semibold text-muted-foreground/55 uppercase tracking-[0.15em]">— This could be you</footer>
        </motion.blockquote>
      </div>
    </section>
  );
};

/* ---------------- Who It's For ---------------- */
const AUDIENCES = [
  { Icon: GraduationCap, label: "Students with ideas" },
  { Icon: Briefcase, label: "Early professionals ready to build" },
  { Icon: Rocket, label: "Builders solving real problems" },
  { Icon: Users, label: "Ages 18–25, ready to act" },
];

const WhoItsFor = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="section-padding bg-background" ref={ref}>
      <div className="container mx-auto px-6 max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-display font-bold text-foreground mb-3">
            This is for{" "}
            <span className="font-accent text-secondary text-3xl sm:text-5xl md:text-6xl text-shimmer" style={{ textShadow: "0 0 20px rgba(238, 205, 37, 0.3)" }}>
              builders.
            </span>
          </h2>
          <p className="text-lg text-muted-foreground font-body mb-12">People who don't just talk about change — they make it happen.</p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {AUDIENCES.map((a, i) => (
            <motion.div
              key={a.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.15 * i, ease: [0.25, 0.1, 0.25, 1] }}
              className="flex flex-col items-center gap-3 group"
            >
              <motion.div
                animate={inView ? { y: [0, -6, 0] } : {}}
                transition={{ duration: 3, delay: 1 + i * 0.4, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
                className="w-16 h-16 rounded-full bg-primary/5 border border-primary/10 flex items-center justify-center group-hover:border-secondary/40 group-hover:bg-secondary/10 group-hover:shadow-[0_0_20px_hsl(49_86%_54%/0.15)] transition-all duration-500"
              >
                <a.Icon className="text-primary group-hover:text-secondary transition-colors duration-300" size={26} />
              </motion.div>
              <span className="font-display font-semibold text-foreground text-sm leading-snug max-w-[140px]">{a.label}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Apply CTA (mid-page conversion) ---------------- */
const ApplyCTA = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <section id="apply" className="py-16 sm:py-24 bg-muted/30" ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
        className="container mx-auto px-6 max-w-xl text-center"
      >
        <h2 className="font-display font-bold text-foreground text-2xl sm:text-3xl mb-3">Applications are open</h2>
        <p className="font-body text-muted-foreground text-base sm:text-lg mb-8">
          Round 1 is live. Create your account to start your application.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to={_route._signup}
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg font-display font-bold text-secondary-foreground transition-all duration-200 hover:brightness-110"
            style={{ background: "linear-gradient(to bottom, hsl(49 88% 58%), hsl(49 86% 48%))" }}
          >
            Apply Now <ArrowRight size={18} />
          </Link>
          <Link
            to={_route._login}
            className="inline-flex items-center justify-center px-8 py-3.5 rounded-lg font-display font-semibold text-primary border border-border bg-background hover:bg-muted/50 transition-colors"
          >
            Sign In
          </Link>
        </div>
      </motion.div>
    </section>
  );
};

/* ---------------- FAQ ---------------- */
const FAQS = [
  { q: "Do I need a team to enter?", a: "No. Individual submissions are accepted. That said, teams with complementary skills tend to produce stronger, more realistic proposals." },
  { q: "Can my project be based somewhere other than Kenya?", a: "For this year's competition, all projects must be designed for implementation in Kenya, where HDF has an established office and operational presence that makes first-time implementation viable." },
  { q: "Can my solution be technology-based or intangible?", a: "Yes. Solutions do not need to be physical or infrastructural. A tech platform, AI-enabled tool, digital system, or process innovation is eligible as long as it directly addresses a documented need in one of our focus areas and is executable within the scope and budget. Think creatively — the best solution is the most impactful one, not necessarily the most visible one." },
  { q: "How specific does my proposal need to be?", a: "Very specific. Vague, high-level concepts will not score well. You need a clear problem, a defined solution, a named target population, a realistic budget, and a step-by-step plan. Judges will ask hard questions during the pitch — be ready to defend every element." },
  { q: "How are proposals judged at each round?", a: "Each round has its own review focus. Round 1 evaluates written proposals against the four scoring criteria (Impact, Feasibility, Innovation, Clarity) using the published judging rubric. Round 2 evaluates the recorded pitch for clarity and presentation — the top 3 finalists are selected at the close of this round. Round 3 is a live interview with the judging panel, conducted alongside a legal background check and budget review by HDF legal counsel — the winner is formally announced after this round. Round 4 is a relationship-building introduction to the broader HDF team. Round 5 is the fundraising window, where the winning team raises their project budget with up to $5,000 from HDF. A rubric is provided for Rounds 1 and 2 so you know exactly what is being evaluated and how." },
  { q: "What if my idea doesn't fit neatly into one focus area?", a: "Review the focus areas and consider where yours connects. If you're unsure, reach out before submitting. Projects that fall entirely outside HDF's program areas will not be eligible." },
  { q: "When do we travel to Kenya?", a: "The trip to Kenya happens after your project is complete. You'll visit the site, meet the community you worked to support, and see your project in action on the ground." },
  { q: "Is this Zakat-eligible?", a: "Yes. This project is Zakat-eligible. Further details will be shared before the fundraising round opens." },
  { q: "What if we win but can't raise our full project budget?", a: "We'll work with you. Options include an extended fundraising window, a partial match arrangement, or in exceptional cases, reallocation. Our goal is to see strong projects implemented, not stalled over a technicality. That said, the fundraising commitment is real — it's part of what makes this a partnership, not just a grant." },
  { q: "Will we have support during implementation?", a: "Yes. Every winning team is paired with HDF mentors who will guide you through the process. You will also have access to HDF's existing partner relationships on the ground in Kenya." },
  { q: "Who owns our idea?", a: "HDF. By submitting, you grant HDF the right to utilize, expand, or implement your idea in any form, at any time, without requiring your consent or providing compensation. You retain the right to speak publicly about your participation and the work you contributed — but the idea itself, and any work produced through this competition, belongs to HDF." },
  { q: "Can we submit more than one proposal?", a: "No. Each individual or team may submit one proposal." },
  { q: "How will submissions be kept confidential?", a: "All proposals are reviewed exclusively by the judging panel and relevant HDF staff. We will not share proposal details publicly without the submitter's explicit consent." },
];

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
      className="relative"
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-[2px] rounded-full transition-all duration-300"
        style={{ backgroundColor: open ? "#EECD25" : "transparent", opacity: open ? 0.7 : 0 }}
      />
      <div className="pl-5 transition-all duration-300">
        <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-4 py-4 sm:py-5 text-left group">
          <span className="font-body font-semibold text-foreground text-base sm:text-lg leading-snug">{q}</span>
          <ChevronDown size={20} className={`shrink-0 text-foreground/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: open ? "600px" : "0px", opacity: open ? 1 : 0 }}>
          <p className="font-body text-sm sm:text-base leading-relaxed pb-6 pr-8" style={{ color: "#5F7682" }}>{a}</p>
        </div>
      </div>
      <div className="h-px bg-border/40 ml-5" />
    </motion.div>
  );
};

const FAQ = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <section className="py-16 sm:py-24 bg-background" ref={ref}>
      <div className="container mx-auto px-6 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="font-display font-bold text-foreground text-2xl sm:text-3xl md:text-4xl tracking-tight mb-3">Frequently Asked Questions</h2>
          <p className="font-body text-sm sm:text-base" style={{ color: "#5F7682" }}>Everything you need to know before applying.</p>
        </motion.div>
        <div className="space-y-0">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} index={i} />
          ))}
        </div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-8 sm:mt-10 font-body text-sm font-medium text-foreground/50"
        >
          Questions? Contact us at <a href="mailto:iman@hdfund.org" className="text-primary font-medium hover:underline">iman@hdfund.org</a>
        </motion.p>
      </div>
    </section>
  );
};

/* ---------------- Final CTA ---------------- */
const FinalCTA = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section className="relative py-32 overflow-hidden" ref={ref}>
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={ctaBg} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-primary/85" />
      </motion.div>
      <div className="container mx-auto px-6 max-w-3xl text-center relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-sm font-display font-semibold text-primary-foreground/50 mb-6 uppercase tracking-[0.2em]"
        >
          You're not just pitching.
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-display font-black text-primary-foreground mb-6 tracking-tight leading-tight"
        >
          You're building, funding, and executing.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-semibold tracking-tight mb-8 text-primary-foreground/90"
        >
          This is <span className="font-accent text-secondary text-3xl sm:text-5xl md:text-6xl lg:text-8xl text-shimmer">your moment.</span>
        </motion.p>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-16 h-0.5 bg-secondary/40 mx-auto mb-8 origin-center"
        />
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-lg text-primary-foreground/60 font-body mb-12"
        >
          Applications are open — your idea could change everything.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link
            to={_route._signup}
            className="group inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 bg-secondary text-secondary-foreground font-display font-bold rounded-lg text-base sm:text-lg hover:brightness-110 transition-all duration-300 animate-pulse-glow hover:scale-105 active:scale-[0.97]"
          >
            Apply Now
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
          <Link
            to={_route._login}
            className="px-8 sm:px-10 py-4 sm:py-5 border-2 border-primary-foreground/30 text-primary-foreground font-display font-semibold rounded-lg text-base sm:text-lg hover:bg-primary-foreground/10 transition-all duration-300 inline-flex items-center justify-center hover:scale-[1.03] active:scale-[0.97]"
          >
            Sign In
          </Link>
          <motion.a
            href="/Website_Brief.pdf"
            download="Human_Development_Fund_Brief.pdf"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="px-8 sm:px-10 py-4 sm:py-5 border-2 border-primary-foreground/20 text-primary-foreground/80 font-display font-medium rounded-lg text-sm sm:text-base hover:bg-primary-foreground/10 transition-all duration-300 inline-flex items-center justify-center"
          >
            Download Brief
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <footer className="py-12 bg-foreground" ref={ref}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="container mx-auto px-6 flex flex-col items-center gap-6"
      >
        <img src={hdfLogo} alt="HDF Logo" className="h-12 w-auto" />
        <div className="text-center">
          <p className="text-sm font-body text-background/60">© {new Date().getFullYear()} Human Development Fund (HDF). All rights reserved.</p>
          <p className="text-xs font-body text-background/40 mt-2">The Human Development Lab — Building change, one idea at a time.</p>
        </div>
      </motion.div>
    </footer>
  );
};

/* ---------------- Floating CTA ---------------- */
const FloatingCTA = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col gap-3"
        >
          <Link
            to={_route._signup}
            className="px-6 py-3 bg-secondary text-secondary-foreground font-display font-bold rounded-full shadow-lg hover:brightness-110 transition-all text-sm inline-flex items-center justify-center"
          >
            Apply Now
          </Link>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="w-10 h-10 rounded-full bg-card border border-border flex items-center justify-center shadow-md hover:shadow-lg transition-all self-end"
          >
            <ArrowUp size={16} className="text-foreground" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---------------- Page ---------------- */
export default function Landing() {
  return (
    <div className="lab-theme overflow-x-hidden">
      <Hero />
      <HdfIntro />
      <EmotionalHook />
      <WhatThisIs />
      <HowItWorks />
      <WhyItMatters />
      <Benefits />
      <WhoItsFor />
      <ApplyCTA />
      <FAQ />
      <FinalCTA />
      <Footer />
      <FloatingCTA />
    </div>
  );
}
